import { TextField } from '@mui/material';

interface CustomTextFieldProps {
  label?: string;
  name: string;
  testId?: string;
  defaultValue?: string;
  type?: 'text' | 'email' | 'password' | 'number';
  currentValue?: string;
  required?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  placeholder?: string;
  error?: boolean;
  errorText?: string;
  size?: 'small' | 'medium' | 'large';
  helperText?: string;
  onChange?: (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
  ) => void;
}

const sizeStyles = {
  small: { height: 32, borderRadius: '10px', fontSize: '0.8125rem', labelTranslateY: 5 },
  medium: { height: 42, borderRadius: '14px', fontSize: '0.875rem', labelTranslateY: 10 },
  large: { height: 48, borderRadius: '16px', fontSize: '1rem', labelTranslateY: 14 },
};

function Input(props: CustomTextFieldProps) {
  const {
    label,
    name,
    testId,
    defaultValue,
    currentValue,
    required = false,
    disabled = false,
    fullWidth = true,
    placeholder,
    helperText,
    errorText,
    error = false,
    type = 'text',
    size = 'medium',
    onChange,
  } = props;

  const value
    = currentValue !== undefined
      ? currentValue
      : defaultValue !== undefined
        ? defaultValue
        : '';

  const { height, borderRadius, fontSize, labelTranslateY } = sizeStyles[size];

  return (
    <TextField
      variant="outlined"
      label={label}
      name={name}
      value={value}
      error={error}
      fullWidth={fullWidth}
      required={required}
      disabled={disabled}
      placeholder={placeholder}
      helperText={error ? errorText : helperText}
      type={type}
      data-testid={testId}
      aria-invalid={error}
      onChange={e => onChange?.(e)}
      className={`input--custom input--custom-${size}`}
      sx={{
        '& .MuiOutlinedInput-root': {
          height,
          fontSize,
          borderRadius,
          background: '#f8fafc',
          transition: 'all 0.2s ease',
          '& fieldset': {
            border: '1.5px solid #e2e8f0',
            borderRadius,
            transition: 'all 0.2s ease',
          },
          '&:hover': {
            background: '#fff',
            '& fieldset': { borderColor: '#cbd5e1' },
          },
          '&.Mui-focused': {
            background: '#fff',
            boxShadow: '0 0 0 3px rgba(192, 90, 58, 0.1)',
            '& fieldset': {
              borderColor: '#c05a3a',
              borderWidth: '1.5px',
            },
          },
          '&.Mui-error fieldset': { borderColor: '#ef4444' },
          '&.Mui-error.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(239, 68, 68, 0.1)',
          },
          '&.Mui-disabled': {
            background: '#f1f5f9',
            '& fieldset': { borderColor: '#e2e8f0' },
          },
          '& input': {
            padding: '0 14px',
            color: '#2a2018',
            '&::placeholder': { color: '#94a3b8', opacity: 1 },
          },
        },
        '& .MuiInputLabel-root': {
          transform: `translate(14px, ${labelTranslateY}px) scale(1)`,
          '&.MuiInputLabel-shrink': {
            transform: 'translate(14px, -9px) scale(0.75)',
          },
        },
        '& .MuiFormHelperText-root': {
          margin: '4px 0 0',
          fontSize: '0.75rem',
          '&.Mui-error': { color: '#ef4444' },
        },
      }}
    />
  );
}

export default Input;
